/**
 * One-line account of the work that followed a reply — "Worked in the
 * workspace", "Edited a file, searched the web". Sits directly under the
 * message that announced it, so the transcript reads as narration plus the
 * work that backed it up rather than a dump of tool calls.
 *
 * The leading verb carries the emphasis; the object stays quiet.
 */
import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import WorkAppear from '@/components/Coworker/WorkAppear';
import { useWorkNow } from '@/hooks/useWorkNow';
import { formatWorkDuration, WORK_THINKING_STATUS } from '@/constants/work';

function WorkStepSummary({
  icon = 'arrow-right',
  text,
  isLive = false,
  liveStartTs = null,
  stepStartTs = null,
}) {
  const { colors } = useTheme();
  const now = useWorkNow(isLive);
  const phrase = text || (isLive ? WORK_THINKING_STATUS : '');
  // Time the current status line, not the whole run. A 7-minute run used
  // to say "Finishing workspace work · 7m" after a one-second bash.
  const stepClock = useRef({ phrase: '', at: 0 });
  useEffect(() => {
    if (!phrase) return;
    if (stepClock.current.phrase === phrase && stepClock.current.at) return;
    stepClock.current = { phrase, at: Date.now() };
  }, [phrase]);

  if (!phrase) return null;

  // Tool timestamps survive navigation and refresh. The local clock is only
  // a fallback for status phrases without an execution event timestamp.
  const startedAt = stepStartTs || stepClock.current.at || liveStartTs;
  const elapsed = isLive && startedAt ? formatWorkDuration(now - startedAt) : null;

  const space = phrase.indexOf(' ');
  const verb = space === -1 ? phrase : phrase.slice(0, space);
  const rest = space === -1 ? '' : phrase.slice(space);

  return (
    <View style={styles.row}>
      {isLive ? (
        <ActivityIndicator size="small" color={colors.color_success} style={styles.icon} />
      ) : (
        <Icon name={icon} size={14} color={colors.text_color_tertiary} style={styles.icon} />
      )}
      {/* Keyed on the summary text: live status changes ease in instead of
          hard-swapping. The elapsed suffix ticks inside the same node. */}
      <WorkAppear key={phrase} enabled={isLive} duration={160} distance={3} style={styles.text}>
        <Text size="n" color="secondary" numberOfLines={2}>
          {verb}
          {!!rest && <Text size="n" color="tertiary">{rest}</Text>}
          {!!elapsed && <Text size="n" color="tertiary">{` · ${elapsed}`}</Text>}
        </Text>
      </WorkAppear>
    </View>
  );
}

WorkStepSummary.propTypes = {
  icon: PropTypes.string,
  text: PropTypes.string,
  isLive: PropTypes.bool,
  liveStartTs: PropTypes.number,
  stepStartTs: PropTypes.number,
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 2,
    marginBottom: 6,
  },
  icon: {
    marginTop: 2,
  },
  text: {
    flex: 1,
  },
});

export default React.memo(WorkStepSummary);
